"use client";

import { useMemo, useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import { KappaSummary } from "@/components/tools/stats-research/kappa-results";
import { PasteBox } from "@/components/tools/stats-research/paste-box";
import { PreviewTable } from "@/components/tools/stats-research/preview-table";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  crossTabulate,
  defaultCategoryOrder,
  orderCategories,
} from "@/lib/tool-kit/stats-research/agreement";
import {
  type HeaderMode,
  parseLabelTable,
  parseNumericTable,
} from "@/lib/tool-kit/stats-research/matrix";

/** English strings. A translated content file overrides these through `ui`. */
export const COHENS_KAPPA_CALCULATOR_STRINGS = {
  modeLabel: "Your data",
  modeTable: "Agreement table (counts)",
  modeLabels: "Two columns of ratings",
  tableLabel:
    "Agreement table — rater A's categories down the side, rater B's across the top",
  tableHint:
    "A square table of counts, pasted from a spreadsheet or typed with tabs, commas or spaces. Category names in the first row and column are optional.",
  tablePlaceholder: "\tYes\tNo\nYes\t20\t5\nNo\t10\t15",
  tableExample:
    "\tLow\tMedium\tHigh\nLow\t14\t3\t1\nMedium\t2\t13\t4\nHigh\t0\t2\t11",
  labelsLabel:
    "Ratings — one subject per row: rater A's category, then rater B's",
  labelsHint:
    "Two columns of category labels or codes. A header row is optional. Rows with a blank or NA rating are left out.",
  labelsPlaceholder: "Rater A\tRater B\nyes\tyes\nno\tyes\nno\tno",
  labelsExample:
    "Teacher 1\tTeacher 2\nPass\tPass\nMerit\tMerit\nFail\tPass\nPass\tPass\nMerit\tPass\nFail\tFail\nPass\tMerit\nPass\tPass\nMerit\tMerit\nFail\tFail\nPass\tPass\nMerit\tMerit\nPass\tFail\nFail\tFail\nMerit\tMerit",
  labelsExampleOrder: "Fail, Pass, Merit",
  orderLabel: "Category order, low to high (for weighted kappa)",
  orderHint:
    "Separate categories with commas. Unlisted categories follow in the order shown. Order used: {order}.",
  loadExample: "Load example",
  clear: "Clear",
  headerLabel: "Header row",
  headerAuto: "Detect automatically",
  headerYes: "First row is a header",
  headerNo: "No header row",
  categoryName: "Category {number}",
  raterA: "Rater A",
  raterB: "Rater B",
  total: "Total",
  tableCaption: "Rows: {rowRater}. Columns: {columnRater}.",
  pairsRead: "one: {count} rated subject|other: {count} rated subjects",
  pairsDropped:
    "one: {count} row with a missing rating was left out.|other: {count} rows with a missing rating were left out.",
  extraColumns: "Only the first two columns are used.",
  notSquare:
    "An agreement table must be square — the same categories down the side and across the top. This one is {rows} × {columns}.",
  notCounts:
    "Every cell must be a whole number of subjects, zero or more. Check line {line}.",
  results: "Results",
  kappa: "Cohen's κ",
  interval: "95% CI",
  observed: "Observed agreement (pₒ)",
  expected: "Chance agreement (pₑ)",
  zLabel: "z (H₀: κ = 0)",
  pLabel: "p (two-sided)",
  interpretation:
    "On Landis and Koch's benchmarks, κ = {kappa} is {band} agreement.",
  bandPoor: "poor",
  bandSlight: "slight",
  bandFair: "fair",
  bandModerate: "moderate",
  bandSubstantial: "substantial",
  bandAlmostPerfect: "almost perfect",
  caveat:
    "Landis and Koch called their cut-offs arbitrary; report κ with its interval rather than the label alone. Kappa also depends on prevalence: when one category dominates, κ can be low even with high raw agreement.",
  maxKappa: "With these margins, the largest κ possible is {value}.",
  weightedHeading: "Weighted kappa (ordered categories)",
  weightedNote:
    "Weighted kappa gives partial credit for near-misses, treating the categories as ordered as shown. Use it only for ordinal categories.",
  columnWeights: "Weights",
  columnKappa: "κ",
  columnSe: "SE",
  columnInterval: "95% CI",
  columnZ: "z",
  columnP: "p",
  weightsNone: "Unweighted",
  weightsLinear: "Linear",
  weightsQuadratic: "Quadratic",
  howCalculated: "How this was calculated",
  workingObserved: "pₒ = agreements / n = {agreements} / {n} = {observed}",
  workingExpected:
    "pₑ = Σ(row total × column total) / n² = {products} / {nSquared} = {expected}",
  workingKappa:
    "κ = (pₒ − pₑ) / (1 − pₑ) = ({observed} − {expected}) / (1 − {expected}) = {kappa}",
  workingSe:
    "The 95% CI is κ ± 1.96 × SE, with the large-sample SE of Fleiss, Cohen and Everitt (1969); z uses the standard error under H₀ (SE₀ = {seNull}).",
  report:
    "Cohen's κ = {kappa}, 95% CI [{lower}, {upper}], z = {z}, p {p} (n = {n}).",
  copyResult: "Copy APA-style result",
  copied: "Copied!",
  enterPrompt:
    "Paste an agreement table, or two columns of ratings, to see Cohen's kappa.",
  degenerate:
    "Kappa is undefined here: both raters put every subject in the same single category, so chance agreement is 100%.",
} as const;

type Strings = typeof COHENS_KAPPA_CALCULATOR_STRINGS;
type Mode = "table" | "labels";

const LIST_SEPARATOR = /[,;\t]/;

interface Crosstab {
  table: number[][];
  categories: string[];
  rowRater: string;
  columnRater: string;
  problem: string | null;
  notes: string[];
}

function fromTable(
  text: string,
  header: HeaderMode,
  strings: Strings,
  fmt: (n: number) => string
): Crosstab {
  const parsed = parseNumericTable(text, header);
  const k = parsed.columns;
  const names = Array.from(
    { length: k },
    (_, j) =>
      parsed.header?.[j] ||
      parsed.rowLabels?.[j] ||
      t(strings.categoryName, { number: String(j + 1) })
  );
  const base = {
    categories: names,
    rowRater: strings.raterA,
    columnRater: strings.raterB,
    notes: [],
  };
  if (parsed.rows.length !== k) {
    return {
      ...base,
      table: [],
      problem: t(strings.notSquare, {
        rows: fmt(parsed.rows.length),
        columns: fmt(k),
      }),
    };
  }
  const bad = parsed.rows.findIndex((row) =>
    row.some((cell) => cell === null || !Number.isInteger(cell) || cell < 0)
  );
  if (bad >= 0) {
    return {
      ...base,
      table: [],
      problem: t(strings.notCounts, { line: fmt(parsed.lines[bad]) }),
    };
  }
  return { ...base, table: parsed.rows as number[][], problem: null };
}

function fromLabels(
  text: string,
  header: HeaderMode,
  orderText: string,
  strings: Strings,
  plural: (key: keyof Strings, count: number) => string
): Crosstab {
  const parsed = parseLabelTable(text, header);
  const pairs: [string, string][] = [];
  let dropped = 0;
  for (const row of parsed.rows) {
    const [a = "", b = ""] = row;
    if (a === "" || b === "") {
      dropped += 1;
    } else {
      pairs.push([a, b]);
    }
  }
  const preferred = orderText
    .split(LIST_SEPARATOR)
    .map((label) => label.trim())
    .filter((label) => label !== "");
  const order = orderCategories(defaultCategoryOrder(pairs.flat()), preferred);
  const notes = [plural("pairsRead", pairs.length)];
  if (dropped > 0) {
    notes.push(plural("pairsDropped", dropped));
  }
  if (parsed.columns > 2) {
    notes.push(strings.extraColumns);
  }
  return {
    table: crossTabulate(pairs, order),
    categories: order,
    rowRater: parsed.header?.[0] || strings.raterA,
    columnRater: parsed.header?.[1] || strings.raterB,
    problem: order.length === 1 ? strings.degenerate : null,
    notes,
  };
}

function withTotals(table: number[][], fmt: (n: number) => string): string[][] {
  const columnTotals =
    table[0]?.map((_, j) => table.reduce((sum, row) => sum + row[j], 0)) ?? [];
  const rows = table.map((row) => [
    ...row.map(fmt),
    fmt(row.reduce((sum, v) => sum + v, 0)),
  ]);
  const n = columnTotals.reduce((sum, v) => sum + v, 0);
  return [...rows, [...columnTotals.map(fmt), fmt(n)]];
}

export default function CohensKappaCalculatorWidget() {
  const strings = useToolUi(COHENS_KAPPA_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("table");
  const [tableText, setTableText] = useState("");
  const [labelsText, setLabelsText] = useState("");
  const [header, setHeader] = useState<HeaderMode>("auto");
  const [orderText, setOrderText] = useState("");

  const crosstab = useMemo(
    () =>
      mode === "table"
        ? fromTable(tableText, header, strings, fmt)
        : fromLabels(labelsText, header, orderText, strings, (key, count) =>
            tPlural(strings[key], locale, count, { count: fmt(count) }).trim()
          ),
    [mode, tableText, labelsText, header, orderText, strings, fmt, locale]
  );
  const text = mode === "table" ? tableText : labelsText;
  const setText = mode === "table" ? setTableText : setLabelsText;
  const ready = crosstab.table.length >= 2 && crosstab.problem === null;

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="ck-mode"
        onChange={setMode}
        options={[
          { value: "table", label: strings.modeTable },
          { value: "labels", label: strings.modeLabels },
        ]}
        value={mode}
      />
      <PasteBox
        actions={[
          {
            label: strings.loadExample,
            onClick: () => {
              setHeader("auto");
              if (mode === "table") {
                setTableText(strings.tableExample);
                return;
              }
              setLabelsText(strings.labelsExample);
              setOrderText(strings.labelsExampleOrder);
            },
          },
          { label: strings.clear, onClick: () => setText("") },
        ]}
        header={{
          label: strings.headerLabel,
          value: header,
          onChange: setHeader,
          options: [
            { value: "auto", label: strings.headerAuto },
            { value: "yes", label: strings.headerYes },
            { value: "no", label: strings.headerNo },
          ],
        }}
        hint={mode === "table" ? strings.tableHint : strings.labelsHint}
        id="ck-data"
        label={mode === "table" ? strings.tableLabel : strings.labelsLabel}
        onChange={setText}
        placeholder={
          mode === "table"
            ? strings.tablePlaceholder
            : strings.labelsPlaceholder
        }
        rows={8}
        value={text}
      />
      {mode === "labels" ? (
        <TextField
          hint={t(strings.orderHint, {
            order: crosstab.categories.join(", ") || "—",
          })}
          id="ck-order"
          label={strings.orderLabel}
          onChange={setOrderText}
          placeholder={crosstab.categories.join(", ")}
          value={orderText}
        />
      ) : null}
      {crosstab.problem && text.trim() !== "" ? (
        <ErrorNotice>{crosstab.problem}</ErrorNotice>
      ) : null}
      {ready ? (
        <section className="space-y-2">
          {crosstab.notes.length > 0 ? (
            <p className={HINT}>{crosstab.notes.join(" ")}</p>
          ) : null}
          <PreviewTable
            caption={t(strings.tableCaption, {
              rowRater: crosstab.rowRater,
              columnRater: crosstab.columnRater,
            })}
            head={[...crosstab.categories, strings.total]}
            limit={50}
            rowHeads={[...crosstab.categories, strings.total]}
            rows={withTotals(crosstab.table, (n) => fmt(n))}
          />
        </section>
      ) : null}
      {ready ? (
        <KappaSummary fmt={fmt} strings={strings} table={crosstab.table} />
      ) : null}
      {ready || (crosstab.problem && text.trim() !== "") ? null : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </div>
  );
}
